import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    VStack,
    HStack,
    Card,
    CardBody,
    CardHeader,
    Heading,
    Text,
    Button,
    useColorModeValue,
    Spinner,
    Alert,
    AlertIcon,
    AlertDescription,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Flex,
    IconButton,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Select,
    Input,
    InputGroup,
    InputLeftElement,
    ButtonGroup,
    Tooltip,
    Grid,
    GridItem,
    Divider,
    Switch,
    FormControl,
    FormLabel,
    Textarea,
    Checkbox,
    Link
} from '@chakra-ui/react';
import {
    MdSearch,
    MdFilterList,
    MdRefresh,
    MdCheckCircle,
    MdCancel,
    MdDelete,
    MdBusiness,
    MdDescription,
    MdSchedule,
    MdMessage,
    MdComment,
    MdEdit,
    MdClose,
    MdFullscreen,
    MdFullscreenExit,
    MdVisibilityOff,
    MdVisibility,
    MdPause,
    MdPlayArrow,
    MdInfoOutline,
    MdContentCopy,
    MdConfirmationNumber
} from 'react-icons/md';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactSelect from 'react-select';
import apiService from '../services/apiService';
import { formatDateTime, formatNumber } from '../utils/helpers';
import PaginationControls from '../components/Common/PaginationControls';
import usageTracker from '../services/usageTracker';
import { useSearchParams } from 'react-router-dom';

/**
 * Format a UTC datetime string (from the backend, may lack a timezone marker) into the
 * viewer's local browser time. Treats the value as UTC by normalizing to ISO + "Z".
 */
const formatUtcToLocal = (value) => {
    if (!value) return '';
    try {
        const normalized = String(value).trim().replace(' ', 'T').split('.')[0] + 'Z';
        const d = new Date(normalized);
        if (isNaN(d)) return String(value);
        return d.toLocaleString('en-US', {
            month: '2-digit', day: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true,
        });
    } catch {
        return String(value);
    }
};

// Redmine trackers offered in the create-ticket form (order matters; defaults to Bug).
const ALLOWED_TRACKERS = ['Task', 'Support', 'Product', 'Development', 'Bug'];

/**
 * Colour for a Redmine status badge. Redmine's own `is_closed` flag decides green vs
 * open; a few well-known names get a more specific colour, and anything unrecognised
 * falls back to blue rather than being mis-coloured.
 */
const redmineStatusColor = (status, isClosed) => {
    if (isClosed) return 'green';
    const s = (status || '').trim().toLowerCase();
    if (s === 'new') return 'orange';
    if (s === 'in progress' || s === 'inprogress') return 'blue';
    if (s === 'resolved' || s === 'closed') return 'green';
    if (s === 'feedback') return 'purple';
    if (s === 'rejected') return 'red';
    return 'blue';
};

/**
 * A Redmine status badge, or a muted hint when the status has never been synced.
 */
const RedmineStatusBadge = ({ status, isClosed, compact }) => {
    if (!status) {
        return compact
            ? <Text fontSize="xs" color="gray.500">—</Text>
            : <Text fontSize="xs" color="gray.500" fontStyle="italic">status not synced</Text>;
    }
    return (
        <Badge colorScheme={redmineStatusColor(status, isClosed)} variant="subtle">
            {status}
        </Badge>
    );
};

/**
 * An issue's existing Redmine ticket: link, last-synced status, and a Sync button that
 * re-reads the status from Redmine. `onSync` is optional — omit it for a read-only view.
 */
const RedmineTicketLink = ({
    redmineId, redmineUrl, status, isClosed, syncedAt, onSync, syncing,
}) => (
    // Wraps as whole items when space is tight; each label/link stays on one line so a
    // ticket number never splits from its ↗.
    <HStack fontSize="sm" spacing={2} flexWrap="wrap" rowGap={1}>
        <MdConfirmationNumber />
        <Text whiteSpace="nowrap">Redmine ticket:</Text>
        {redmineUrl ? (
            <Link href={redmineUrl} isExternal color="blue.500" fontWeight="600" whiteSpace="nowrap">
                #{redmineId} ↗
            </Link>
        ) : (
            <Text fontWeight="600" whiteSpace="nowrap">#{redmineId}</Text>
        )}
        <RedmineStatusBadge status={status} isClosed={isClosed} />
        {onSync && (
            <Tooltip
                label={syncedAt ? `Last synced ${formatUtcToLocal(syncedAt)}` : 'Never synced'}
                hasArrow
            >
                <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    leftIcon={<MdRefresh />}
                    onClick={onSync}
                    isLoading={syncing}
                    loadingText="Syncing…"
                >
                    Sync Status
                </Button>
            </Tooltip>
        )}
    </HStack>
);

/**
 * Comment + optional Redmine-ticket input, isolated in its own component with local
 * state so that typing/selecting does NOT re-render the (large) Issues page/table.
 * When "Create a Redmine ticket on save" is checked it shows the ticket form
 * (subject + tracker/priority/assignee dropdowns) populated from `options`.
 * Calls onSubmit({ comment, createTicket, subject, trackerId, priorityId, assigneeId,
 * customFields }) and clears itself when the add succeeds.
 */
const CommentBox = ({
    submitting, issueId, alreadyHasTicket, redmineId, redmineUrl, defaultSubject, onSubmit,
    redmineStatus, redmineStatusIsClosed, redmineStatusSyncedAt, onSyncStatus, syncingStatus,
}) => {
    const [text, setText] = useState('');
    const [createTicket, setCreateTicket] = useState(false);
    const [subject, setSubject] = useState('');
    const [trackerId, setTrackerId] = useState('');
    const [priorityId, setPriorityId] = useState('');
    const [assigneeId, setAssigneeId] = useState('');
    const [customFieldValues, setCustomFieldValues] = useState({}); // { [fieldId]: value } for required Redmine custom fields
    // Redmine pick-lists are fetched HERE (local state) so ticking the checkbox / picking
    // an option never re-renders the large Issues page.
    const [options, setOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [optionsError, setOptionsError] = useState('');

    // Dark-mode-aware styling for the searchable assignee (react-select).
    const rsBg = useColorModeValue('#ffffff', '#2D3748');
    const rsText = useColorModeValue('#1A202C', '#E2E8F0');
    const rsBorder = useColorModeValue('#E2E8F0', '#4A5568');
    const rsHover = useColorModeValue('#EDF2F7', '#3A4759');
    const rsMuted = useColorModeValue('#718096', '#A0AEC0');
    const rsStyles = {
        menuPortal: (b) => ({ ...b, zIndex: 9999 }),
        control: (b) => ({ ...b, backgroundColor: rsBg, borderColor: rsBorder }),
        menu: (b) => ({ ...b, backgroundColor: rsBg, zIndex: 9999 }),
        singleValue: (b) => ({ ...b, color: rsText }),
        input: (b) => ({ ...b, color: rsText }),
        placeholder: (b) => ({ ...b, color: rsMuted }),
        option: (b, s) => ({
            ...b,
            backgroundColor: s.isSelected ? '#3182CE' : s.isFocused ? rsHover : rsBg,
            color: s.isSelected ? '#ffffff' : rsText,
            cursor: 'pointer',
        }),
    };

    // Trackers are restricted to these five, in this order, defaulting to Bug.
    const trackerOptions = (options?.trackers || [])
        .filter(t => ALLOWED_TRACKERS.includes(t.name))
        .sort((a, b) => ALLOWED_TRACKERS.indexOf(a.name) - ALLOWED_TRACKERS.indexOf(b.name));
    const assigneeOptions = (options?.assignees || []).map(a => ({ value: String(a.id), label: a.name }));
    const selectedAssignee = assigneeOptions.find(o => o.value === assigneeId) || null;

    // Fetch the pick-lists the first time the ticket form is opened (kept local).
    const loadOptions = async () => {
        if (!issueId) return;
        setOptionsLoading(true);
        setOptionsError('');
        try {
            const res = await apiService.getRedmineOptions(issueId);
            if (res.success && res.data) {
                setOptions(res.data);
            } else {
                setOptionsError(res.message || 'Could not load Redmine options for this client.');
            }
        } catch (e) {
            setOptionsError(e?.message || 'Could not load Redmine options for this client.');
        } finally {
            setOptionsLoading(false);
        }
    };

    // Apply sensible defaults once the Redmine pick-lists arrive.
    useEffect(() => {
        if (!options) return;
        const bug = trackerOptions.find(t => t.name === 'Bug');
        setTrackerId(prev => prev || String(bug?.id ?? trackerOptions[0]?.id ?? ''));
        const defPri = (options.priorities || []).find(p => p.isDefault);
        setPriorityId(prev => prev || String(defPri?.id ?? options.defaultPriorityId ?? options.priorities?.[0]?.id ?? ''));
        setAssigneeId(prev => prev || String(options.defaultAssigneeId ?? options.assignees?.[0]?.id ?? ''));
        // Pre-fill required custom fields (list fields default to their first value).
        const cfDefaults = {};
        (options.requiredCustomFields || []).forEach(f => {
            if (Array.isArray(f.possibleValues) && f.possibleValues.length > 0) {
                cfDefaults[f.id] = f.possibleValues[0];
            }
        });
        setCustomFieldValues(prev => ({ ...cfDefaults, ...prev }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    const toggleCreate = (checked) => {
        setCreateTicket(checked);
        if (checked) {
            setSubject(prev => prev || defaultSubject || '');
            if (!options && !optionsLoading) loadOptions();
        }
    };

    const needSubject = createTicket && !alreadyHasTicket;
    const canCreate = needSubject && !!options && !optionsError;
    const requiredCustomFields = options?.requiredCustomFields || [];
    const missingCustomField = requiredCustomFields.some(
        f => !(customFieldValues[f.id] && String(customFieldValues[f.id]).trim())
    );

    const submit = async () => {
        const ok = await onSubmit({
            comment: text,
            createTicket: canCreate,
            subject,
            trackerId: trackerId ? Number(trackerId) : null,
            priorityId: priorityId ? Number(priorityId) : null,
            assigneeId: assigneeId ? Number(assigneeId) : null,
            customFields: requiredCustomFields.map(f => ({ id: f.id, value: customFieldValues[f.id] ?? '' })),
        });
        if (ok) {
            setText('');
            setCreateTicket(false);
            setSubject('');
        }
    };

    const disabled = !text.trim() || (canCreate && (!subject.trim() || missingCustomField));

    return (
        <Box>
            <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Add a comment…"
                size="sm"
                rows={3}
                resize="vertical"
            />

            {alreadyHasTicket ? (
                <Box mt={2}>
                    <RedmineTicketLink
                        redmineId={redmineId}
                        redmineUrl={redmineUrl}
                        status={redmineStatus}
                        isClosed={redmineStatusIsClosed}
                        syncedAt={redmineStatusSyncedAt}
                        onSync={onSyncStatus}
                        syncing={syncingStatus}
                    />
                </Box>
            ) : (
                <Checkbox mt={2} isChecked={createTicket} onChange={(e) => toggleCreate(e.target.checked)}>
                    <Text fontSize="sm">Create a Redmine ticket on save</Text>
                </Checkbox>
            )}

            {needSubject && (
                <VStack align="stretch" spacing={2} mt={2}>
                    {optionsLoading && !options ? (
                        <HStack color={rsMuted} fontSize="sm"><Spinner size="sm" /><Text>Loading Redmine options…</Text></HStack>
                    ) : optionsError ? (
                        <Alert status="warning" borderRadius="md" fontSize="sm">
                            <AlertIcon />
                            <AlertDescription>{optionsError}</AlertDescription>
                        </Alert>
                    ) : options ? (
                        <>
                            <Input
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Ticket subject (required)"
                                size="sm"
                            />
                            <Select size="sm" value={trackerId} onChange={(e) => setTrackerId(e.target.value)} placeholder="Select tracker">
                                {trackerOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </Select>
                            <Select size="sm" value={priorityId} onChange={(e) => setPriorityId(e.target.value)} placeholder="Select priority">
                                {(options?.priorities || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </Select>
                            <ReactSelect
                                isSearchable
                                placeholder="Search & select assignee…"
                                options={assigneeOptions}
                                value={selectedAssignee}
                                onChange={(opt) => setAssigneeId(opt ? opt.value : '')}
                                menuPlacement="auto"
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={rsStyles}
                            />
                            {requiredCustomFields.map(f => (
                                Array.isArray(f.possibleValues) && f.possibleValues.length > 0 ? (
                                    <Select
                                        key={f.id}
                                        size="sm"
                                        value={customFieldValues[f.id] || ''}
                                        onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                                        placeholder={`Select ${f.name}`}
                                    >
                                        {f.possibleValues.map(v => <option key={v} value={v}>{v}</option>)}
                                    </Select>
                                ) : (
                                    <Input
                                        key={f.id}
                                        size="sm"
                                        value={customFieldValues[f.id] || ''}
                                        onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                                        placeholder={`${f.name} (required)`}
                                    />
                                )
                            ))}
                        </>
                    ) : null}
                </VStack>
            )}

            <Flex justify="flex-end" mt={2}>
                <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={submit}
                    isLoading={submitting}
                    isDisabled={disabled}
                >
                    {canCreate ? 'Add Comment & Create Ticket' : 'Add Comment'}
                </Button>
            </Flex>
        </Box>
    );
};

/**
 * A single comment row with an inline edit mode. Edit state is local so editing one
 * comment doesn't re-render the whole list/page. The Edit button is shown only when
 * canEdit is true (the current user is the comment's author).
 */
const CommentItem = ({ comment, canEdit, onSave, bg }) => {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(comment.comment || '');
    const [saving, setSaving] = useState(false);

    const startEdit = () => { setText(comment.comment || ''); setEditing(true); };
    const cancel = () => { setEditing(false); setText(comment.comment || ''); };
    const save = async () => {
        const t = text.trim();
        if (!t) return;
        setSaving(true);
        const ok = await onSave(comment.id, t);
        setSaving(false);
        if (ok) setEditing(false);
    };

    return (
        <Box p={3} bg={bg} borderRadius="md">
            {editing ? (
                <>
                    <Textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        size="sm"
                        rows={3}
                        resize="vertical"
                    />
                    <Flex justify="flex-end" mt={2} gap={2}>
                        <Button size="xs" variant="ghost" onClick={cancel} isDisabled={saving}>Cancel</Button>
                        <Button size="xs" colorScheme="blue" onClick={save} isLoading={saving} isDisabled={!text.trim()}>Save</Button>
                    </Flex>
                </>
            ) : (
                <>
                    <Text fontSize="sm" whiteSpace="pre-wrap" overflowWrap="break-word">{comment.comment}</Text>
                    <HStack justify="space-between" mt={1}>
                        <Text fontSize="xs" color="gray.500">{comment.createdBy || 'Unknown'}</Text>
                        <HStack spacing={1}>
                            <Text fontSize="xs" color="gray.500">
                                {comment.createdAtUtc ? formatUtcToLocal(comment.createdAtUtc) : ''}
                            </Text>
                            {canEdit && comment.id ? (
                                <Tooltip label="Edit" hasArrow>
                                    <IconButton
                                        icon={<MdEdit />}
                                        size="xs"
                                        variant="ghost"
                                        aria-label="Edit comment"
                                        onClick={startEdit}
                                    />
                                </Tooltip>
                            ) : null}
                        </HStack>
                    </HStack>
                </>
            )}
        </Box>
    );
};

/**
 * Issues Management Page Component
 */
function Issues({ userProfile, appConfig, authServiceReady }) {
    // Theme-aware styling 
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const hoverBg = useColorModeValue('gray.50', 'gray.700');
    const fullscreenBg = useColorModeValue('gray.50', 'gray.900');
    
    // Additional theme colors for filter persistence notice
    const persistenceNoticeBg = useColorModeValue('blue.50', 'blue.900');
    const persistenceNoticeTextPrimary = useColorModeValue('blue.700', 'blue.200');
    const persistenceNoticeTextSecondary = useColorModeValue('blue.600', 'blue.300');

    // Check admin role from localStorage
    const isAdmin = localStorage.getItem('userRole') === 'admin';

    // Tickets tab: which ticket states to show. 'open' by default — a ticket that has
    // never been synced counts as open, since its real status is unknown.
    const [ticketClosedFilter, setTicketClosedFilter] = useState('open'); // 'open' | 'closed' | 'all'
    const [searchParams] = useSearchParams();

    // Tab configuration with issue ID column removed and dynamic action column based on user role
    const tabs = [
        {
            key: 'issues',
            label: 'Issues',
            count: 0,
            // Every open issue, assigned or not. Issues are resolved directly from here,
            // so there is no longer an "assigned" state to filter on.
            defaultFilters: {
                is_issue_closed: false
            },
            columns: ['client', 'report', 'message', 'ticket', 'created', 'actions']
        },
        {
            key: 'fixed',
            label: 'Issues Fixed',
            count: 0,
            defaultFilters: { 
                is_issue_closed: true, 
                fixed_by: 'notNull' 
            },
            columns: isAdmin 
                ? ['client', 'report', 'message', 'issueTakenBy', 'fixedBy', 'created', 'actions']
                : ['client', 'report', 'message', 'issueTakenBy', 'fixedBy', 'created']
        },
        {
            key: 'notIssue',
            label: 'Not an Issues',
            count: 0,
            defaultFilters: { 
                is_issue_closed: true, 
                not_an_issue_by: 'notNull' 
            },
            columns: isAdmin
                ? ['client', 'report', 'message', 'issueTakenBy', 'notAnIssueBy', 'created', 'actions']
                : ['client', 'report', 'message', 'issueTakenBy', 'notAnIssueBy', 'created']
        },
        {
            key: 'tickets',
            label: 'Tickets',
            count: 0,
            // Tickets, not issues: no is_issue_closed filter, so a ticket still open in
            // Redmine shows here even after its Prism issue was marked Fixed.
            defaultFilters: {
                has_ticket: true,
                ...(ticketClosedFilter !== 'all' && { ticket_closed: ticketClosedFilter === 'closed' })
            },
            // Shows the Redmine ticket status rather than the Prism issue status — on this
            // tab the ticket's state is the subject, and showing both side by side reads
            // as a contradiction (e.g. a Fixed issue whose ticket is still New).
            columns: isAdmin
                ? ['client', 'report', 'message', 'ticketStatus', 'ticket', 'created', 'actions']
                : ['client', 'report', 'message', 'ticketStatus', 'ticket', 'created']
        }
    ];

    // State management
    const [activeTab, setActiveTab] = useState(0);
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabCounts, setTabCounts] = useState([0, 0, 0, 0]);
    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        totalPages: 0
    });

    // New state for fullscreen mode and controls with auto refresh enabled by default
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showFilters, setShowFilters] = useState(true);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [showInternal, setShowInternal] = useState(false); // When false, internal clients' issues are hidden

    // Filter state with backend parameter names - keep persistent across tab switches
    const [userFilters, setUserFilters] = useState({
    client_id: '',
    report_code: '',
    search: '',
    startDate: null,
    endDate: null
    });

    // Add new state for date range picker
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [clients, setClients] = useState([]);
    const [reports, setReports] = useState([]);

    // Track if URL parameters have been processed
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

    // Action modal state
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [actionType, setActionType] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // custom styles for DatePicker to match theme
    const datePickerCustomStyles = `
    .custom-datepicker-wrapper {
        display: block;
        width: 100%;
    }

    .custom-datepicker {
        width: 100%;
        height: 2rem;
        min-height: 2rem;
        padding-left: 1rem;
        padding-right: 1rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        border:1px solid #dcdcdc;
        border-radius: 0.375rem;
        border-color: ${borderColor};
        background-color: ${cardBg};
        color: ${textColor};
        transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
        transition-duration: 200ms;
        cursor: pointer;
        outline: 2px solid transparent;
        outline-offset: 2px;
    }

    .custom-datepicker:hover {
        border-color: ${useColorModeValue('gray.300', 'whiteAlpha.400')};
    }

    .custom-datepicker:focus {
        z-index: 1;
        border-color: var(--chakra-colors-brand-500);
        box-shadow: 0 0 0 1px var(--chakra-colors-brand-500);
    }

    .custom-datepicker::placeholder {
        color: ${useColorModeValue('gray.400', 'whiteAlpha.400')};
    }

    .react-datepicker-popper {
        z-index: 9999 !important;
    }

    .react-datepicker {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
        border: 1px solid ${borderColor};
        border-radius: 0.5rem;
        background-color: ${cardBg};
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    }

    .react-datepicker__header {
        background-color: ${hoverBg};
        border-bottom: 1px solid ${borderColor};
        border-radius: 0.5rem 0.5rem 0 0;
        padding-top: 0.75rem;
    }

    .react-datepicker__current-month {
        color: ${headingColor};
        font-weight: 600;
        font-size: 0.95rem;
        margin-bottom: 0.5rem;
    }

    .react-datepicker__day-name {
        color: ${textColor};
        font-weight: 500;
        font-size: 0.85rem;
        width: 2rem;
        line-height: 2rem;
    }

    .react-datepicker__day {
        color: ${textColor};
        width: 2rem;
        line-height: 2rem;
        font-size: 0.85rem;
        margin: 0.15rem;
        border-radius: 0.375rem;
    }

    .react-datepicker__day:hover {
        background-color: ${hoverBg};
        border-radius: 0.375rem;
    }

    .react-datepicker__day--selected,
    .react-datepicker__day--in-selecting-range,
    .react-datepicker__day--in-range {
        background-color: var(--chakra-colors-brand-500) !important;
        color: white !important;
        font-weight: 500;
        border-radius: 0.375rem;
    }

    .react-datepicker__day--range-start {
        border-radius: 0.375rem !important;
    }

    .react-datepicker__day--range-end {
        border-radius: 0.375rem !important;
    }

    .react-datepicker__day--keyboard-selected {
        background-color: var(--chakra-colors-brand-300);
        color: white;
        border-radius: 0.375rem;
    }

    .react-datepicker__day--disabled {
        color: var(--chakra-colors-gray-400);
        cursor: not-allowed;
    }

    .react-datepicker__day--disabled:hover {
        background-color: transparent;
    }

    .react-datepicker__day--outside-month {
        color: var(--chakra-colors-gray-400);
    }

    .react-datepicker__navigation {
        top: 0.75rem;
    }

    .react-datepicker__navigation-icon::before {
        border-color: ${textColor};
        border-width: 2px 2px 0 0;
    }

    .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
        border-color: var(--chakra-colors-brand-500);
    }

    .react-datepicker__month {
        margin: 0.75rem;
    }

    .react-datepicker__day-names {
        margin-bottom: 0.25rem;
    }

    .react-datepicker__week {
        display: flex;
    }

    .react-datepicker__triangle {
        display: none;
    }

    .react-datepicker__month-dropdown,
    .react-datepicker__year-dropdown {
        background-color: ${cardBg};
        border: 1px solid ${borderColor};
        border-radius: 0.375rem;
        padding: 0.25rem;
    }

    .react-datepicker__month-option,
    .react-datepicker__year-option {
        color: ${textColor};
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
    }

    .react-datepicker__month-option:hover,
    .react-datepicker__year-option:hover {
        background-color: ${hoverBg};
    }

    .react-datepicker__month-option--selected,
    .react-datepicker__year-option--selected {
        background-color: var(--chakra-colors-brand-500);
        color: white;
    }

    .react-datepicker__close-icon {
        right: 0.5rem;
    }

    .react-datepicker__close-icon::after {
        background-color: var(--chakra-colors-gray-400);
        font-size: 1rem;
        width: 1rem;
        height: 1rem;
        line-height: 1rem;
    }

    .react-datepicker__close-icon:hover::after {
        background-color: var(--chakra-colors-gray-500);
    }
    `;

    // Message modal state
    const {
        isOpen: isMessageOpen,
        onOpen: onMessageOpen,
        onClose: onMessageClose
    } = useDisclosure();
    const [messageToShow, setMessageToShow] = useState('');
    const [messageIssue, setMessageIssue] = useState(null); // full issue for the message modal (for "Run Data Query")

    // On-demand "Run Data Query" state. The backend runs the issue's data query once via
    // the dynamic-report reload channel; results arrive after the client's next poll.
    const [dqHasQuery, setDqHasQuery] = useState(null);  // null=unknown, true/false once loaded
    const [dqLoading, setDqLoading] = useState(false);   // loading existing result on open
    const [dqRunning, setDqRunning] = useState(false);   // queued + polling for a fresh result
    const [dqResult, setDqResult] = useState(null);      // { columns: [], rows: [], executedAt }
    const dqPollRef = useRef(null);

    // Issue comments state (shown in a dedicated Comments modal)
    const [commentIssue, setCommentIssue] = useState(null);
    const [comments, setComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const {
        isOpen: isCommentsOpen,
        onOpen: onCommentsOpen,
        onClose: onCommentsClose,
    } = useDisclosure();

    // issueId whose Redmine status is currently being synced (null when idle)
    const [syncingStatusFor, setSyncingStatusFor] = useState(null);

    // Whether the message modal's Data Query section is showing the SQL (info toggle)
    const [showDataQuerySql, setShowDataQuerySql] = useState(false);

    // Modified auto-refresh timer with manual control
    const refreshInterval = useRef(null);
    const toast = useToast();

    /**
     * Toggle fullscreen mode
     */
    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(prev => !prev);
    }, []);

    /**
     * Toggle filter panel visibility
     */
    const toggleFilters = useCallback(() => {
        setShowFilters(prev => !prev);
    }, []);

    /**
     * Toggle auto refresh
     */
    const toggleAutoRefresh = useCallback(() => {
        setAutoRefreshEnabled(prev => !prev);
    }, []);

    /**
     * Toggle whether internal clients' issues are shown (resets to first page).
     */
    const handleShowInternalToggle = useCallback((checked) => {
        setShowInternal(checked);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    /**
     * Load filter options (clients and reports)
     */
    const loadFilterOptions = useCallback(async () => {
        try {
            const [clientsResponse, reportsResponse] = await Promise.all([
                apiService.getActiveClients(),
                apiService.getReports({ size: 1000 })
            ]);

            if (clientsResponse.success) {
                setClients(clientsResponse.data || []);
            }

            if (reportsResponse.success) {
                setReports(reportsResponse.data?.content || []);
            }
        } catch (error) {
            console.error('Failed to load filter options:', error);
        }
    }, []);


    // Add handler for date range changes
    const handleDateRangeChange = useCallback((update) => {
        setDateRange(update);
        const [start, end] = update;
        
        // Convert dates to ISO format for API
        setUserFilters(prev => ({
            ...prev,
            startDate: start ? start.toISOString() : '',
            endDate: end ? end.toISOString() : ''
        }));
        
        // Only trigger filter change if both dates are selected or both are cleared
        if ((start && end) || (!start && !end)) {
            setPagination(prev => ({ ...prev, page: 1 }));
        }
    }, []);

    /**
     * Build complete filter parameters for API call
     */
    const buildFilterParams = useCallback((tabIndex, userFilters, pagination) => {
    const tab = tabs[tabIndex];
    const params = {
        page: pagination.page,
        size: pagination.size,
        sortBy: 'created_at_utc',
        sortDirection: 'DESC',
        ...tab.defaultFilters,
        ...(userFilters.client_id && { client_id: parseInt(userFilters.client_id) }),
        ...(userFilters.report_code && { report_code: parseInt(userFilters.report_code) }),
        ...(userFilters.search && { search: userFilters.search.trim() }),
        ...(userFilters.startDate && { startDate: userFilters.startDate }),
        ...(userFilters.endDate && { endDate: userFilters.endDate }),
        // Hide internal clients' issues unless "Show Internal" is on.
        ...(!showInternal && { exclude_internal: true })
    };

    return params;
    }, [showInternal, ticketClosedFilter]);

    /**
     * Load issues data for current tab with proper backend parameters
     */
    const loadIssues = useCallback(async (tabIndex = activeTab, isAutoRefresh = false) => {
        try {
            if (!isAutoRefresh) {
                setError(null);
                setLoading(true);
            }

            const params = buildFilterParams(tabIndex, userFilters, pagination);
            
            const response = await apiService.getReportTriggeredIssues(params);

            if (response.success) {
                const issuesData = response.data?.content || [];
                setIssues(issuesData);
                setPagination({
                    page: response.data?.pagination?.page || 1,
                    size: response.data?.pagination?.size || 20,
                    total: response.data?.pagination?.total || 0,
                    totalPages: response.data?.pagination?.totalPages || 0
                });
            } else {
                throw new Error(response.message || 'Failed to load issues');
            }

        } catch (error) {
            console.error('Failed to load issues:', error);
            if (!isAutoRefresh) {
                setError(error.message || 'Failed to load issues');
                toast({
                    title: 'Error',
                    description: 'Failed to load issues',
                    status: 'error',
                    duration: 5000,
                    isClosable: true
                });
            }
        } finally {
            if (!isAutoRefresh) {
                setLoading(false);
            }
        }
    }, [activeTab, pagination.page, pagination.size, userFilters, buildFilterParams, toast]);

    /**
     * Load tab counts for badges with proper backend parameters
     */
    const loadTabCounts = useCallback(async () => {
        try {
            const countPromises = tabs.map((tab, index) => {
                const params = buildFilterParams(index, userFilters, { page: 1, size: 1 });
                return apiService.getReportTriggeredIssues(params);
            });

            const responses = await Promise.all(countPromises);
            const counts = responses.map((response) => {
                return response.success ? (response.data?.pagination?.total || 0) : 0;
            });
            
            setTabCounts(counts);
        } catch (error) {
            console.error('Failed to load tab counts:', error);
        }
    }, [userFilters, buildFilterParams]);

    /**
     * Handle tab change - Keep client and report filters persistent
     */
    const handleTabChange = useCallback((index) => {
        usageTracker.tab('Issues', tabs[index]?.label || `Tab ${index}`);
        setActiveTab(index);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    }, []);

    /**
     * Handle pagination change
     */
    const handlePageChange = useCallback((newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    }, []);

    /**
     * Handle page size change
     */
    const handlePageSizeChange = useCallback((newSize) => {
        setPagination(prev => ({ ...prev, page: 1, size: newSize }));
    }, []);

    /**
     * Handle filter change with backend parameter names
     */
    const handleFilterChange = useCallback((field, value) => {
        setUserFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    }, []);

    /**
     * Clear all user filters - explicit manual action
     */
    const handleClearFilters = useCallback(() => {
    setUserFilters({
        client_id: '',
        report_code: '',
        search: '',
        startDate: '',
        endDate: ''
    });
    setDateRange([null, null]);
    setTicketClosedFilter('open');
    setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    // Derive ordered column keys from a list of row objects
    const deriveColumns = (rows) => {
        const seen = new Set();
        const columns = [];
        (rows || []).forEach(row => Object.keys(row || {}).forEach(k => {
            if (!seen.has(k)) { seen.add(k); columns.push(k); }
        }));
        return columns;
    };

    /**
     * Load the stored data-query result for an issue (called when the message modal opens).
     */
    const loadDqResult = useCallback(async (issueId) => {
        if (!issueId) return;
        setDqLoading(true);
        try {
            const res = await apiService.getIssueDataQueryResult(issueId);
            const data = res?.data || {};
            setDqHasQuery(data.hasDataQuery !== false);
            if (data.executed && Array.isArray(data.rows)) {
                setDqResult({ columns: deriveColumns(data.rows), rows: data.rows, executedAt: data.executedAt });
            } else {
                setDqResult(null);
            }
        } catch {
            setDqHasQuery(null);
            setDqResult(null);
        } finally {
            setDqLoading(false);
        }
    }, []);

    /**
     * Load the comments for an issue into the message modal.
     */
    const loadComments = useCallback(async (issueId) => {
        if (!issueId) { setComments([]); return; }
        setCommentsLoading(true);
        try {
            const res = await apiService.getIssueComments(issueId);
            setComments(res.success && Array.isArray(res.data) ? res.data : []);
        } catch {
            setComments([]);
        } finally {
            setCommentsLoading(false);
        }
    }, []);

    /**
     * Handle message click to show in modal (and load any existing data-query result)
     */
    const handleMessageClick = useCallback((issue) => {
        // Accept either a full issue object or a bare message string (back-compat)
        const isObj = issue && typeof issue === 'object';
        const issueObj = isObj ? issue : null;
        setMessageIssue(issueObj);
        setMessageToShow(isObj ? (issue.message || '') : (issue || ''));
        // reset prior data-query state
        if (dqPollRef.current) { clearInterval(dqPollRef.current); dqPollRef.current = null; }
        setDqRunning(false);
        setDqResult(null);
        setDqHasQuery(null);
        setShowDataQuerySql(false);
        onMessageOpen();
        if (issueObj?.issueId) loadDqResult(issueObj.issueId);
    }, [onMessageOpen, loadDqResult]);

    // Clear the data-query poll interval on unmount
    useEffect(() => () => { if (dqPollRef.current) clearInterval(dqPollRef.current); }, []);

    // Stop polling and reset state when the message modal closes
    const handleMessageClose = useCallback(() => {
        if (dqPollRef.current) { clearInterval(dqPollRef.current); dqPollRef.current = null; }
        setDqRunning(false);
        setDqResult(null);
        setDqHasQuery(null);
        setMessageIssue(null);
        setShowDataQuerySql(false);
        onMessageClose();
    }, [onMessageClose]);

    /**
     * Open the Comments modal for an issue and load its comments.
     */
    const handleCommentsClick = useCallback((issue) => {
        setCommentIssue(issue);
        setComments([]);
        onCommentsOpen();
        if (issue?.issueId) loadComments(issue.issueId);
    }, [onCommentsOpen, loadComments]);

    /**
     * Close the Comments modal and reset its state.
     */
    const handleCommentsClose = useCallback(() => {
        setCommentIssue(null);
        setComments([]);
        onCommentsClose();
    }, [onCommentsClose]);

    /**
     * Jump from the Issue Message modal to the Comments modal, where a ticket can be
     * raised and comments added. The message modal closes so only one is ever open.
     */
    const handleTicketFromMessage = useCallback((issue) => {
        handleMessageClose();
        handleCommentsClick(issue);
    }, [handleMessageClose, handleCommentsClick]);

    /**
     * Re-read an issue's Redmine ticket status and reflect it everywhere it is shown
     * (the table row, and whichever modal is open).
     */
    const handleSyncRedmineStatus = useCallback(async (issue) => {
        const id = issue?.issueId;
        if (!id || !issue?.redmineIssueId) return;

        setSyncingStatusFor(id);
        try {
            const res = await apiService.syncRedmineStatus(id);
            if (!res.success) {
                toast({ title: 'Failed to sync status', description: res.message || '', status: 'error', duration: 6000, isClosable: true, position: 'top-right' });
                return;
            }

            const { redmineStatus, redmineStatusIsClosed, redmineStatusSyncedAtUtc, ticketMissing } = res.data || {};
            const patch = { redmineStatus, redmineStatusIsClosed, redmineStatusSyncedAtUtc };

            setIssues(prev => prev.map(it => (it.issueId === id ? { ...it, ...patch } : it)));
            setMessageIssue(prev => (prev && prev.issueId === id ? { ...prev, ...patch } : prev));
            setCommentIssue(prev => (prev && prev.issueId === id ? { ...prev, ...patch } : prev));

            if (ticketMissing) {
                toast({ title: 'Ticket no longer exists in Redmine', status: 'warning', duration: 8000, isClosable: true, position: 'top-right' });
            } else {
                usageTracker.action('Issues', 'SYNC_TICKET_STATUS', `#${issue.redmineIssueId}`);
                toast({ title: `Status: ${redmineStatus}`, status: 'success', duration: 3000, isClosable: true, position: 'top-right' });
            }
        } catch (err) {
            toast({ title: 'Failed to sync status', description: err?.message || '', status: 'error', duration: 6000, isClosable: true, position: 'top-right' });
        } finally {
            setSyncingStatusFor(null);
        }
    }, [toast]);

    /**
     * Add a comment to the currently-open issue. Receives the text from the isolated
     * CommentInput; returns true on success so the input can clear itself.
     */
    const handleAddComment = useCallback(async (payload) => {
        const { comment, createTicket, subject, trackerId, priorityId, assigneeId, customFields } = payload || {};
        const trimmed = (comment || '').trim();
        if (!trimmed || !commentIssue?.issueId) return false;
        setCommentSubmitting(true);
        try {
            const userEmail = localStorage.getItem('userEmail') || userProfile?.email || '';
            const res = await apiService.addIssueComment(commentIssue.issueId, trimmed, userEmail);
            if (!res.success) {
                toast({ title: 'Failed to add comment', description: res.message || '', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
                return false;
            }
            usageTracker.action('Issues', 'ADD_COMMENT', commentIssue.clientName || undefined);
            // Reload so every comment (including this new one) carries its id for editing.
            loadComments(commentIssue.issueId);

            // Optionally also raise a Redmine ticket, using this comment as the description.
            if (createTicket && !commentIssue.redmineIssueId) {
                try {
                    const tRes = await apiService.createRedmineTicket(commentIssue.issueId, {
                        subject, trackerId, priorityId, assignedToId: assigneeId, description: trimmed, customFields,
                    });
                    if (tRes.success && tRes.data?.redmineIssueId) {
                        const rid = tRes.data.redmineIssueId;
                        const rurl = tRes.data.redmineIssueUrl || null;
                        setCommentIssue(prev => prev ? { ...prev, redmineIssueId: rid, redmineIssueUrl: rurl } : prev);
                        setIssues(prev => prev.map(it => it.issueId === commentIssue.issueId
                            ? { ...it, redmineIssueId: rid, redmineIssueUrl: rurl } : it));
                        usageTracker.action('Issues', 'CREATE_TICKET', `Ticket #${rid} for ${commentIssue.clientName || ''}`.trim());
                        toast({ title: `Redmine ticket #${rid} created`, status: 'success', duration: 4000, isClosable: true, position: 'top-right' });
                    } else {
                        toast({ title: 'Comment added, but Redmine ticket failed', description: tRes.message || '', status: 'warning', duration: 10000, isClosable: true, position: 'top-right' });
                    }
                } catch (err) {
                    toast({ title: 'Comment added, but Redmine ticket failed', description: err?.message || '', status: 'warning', duration: 10000, isClosable: true, position: 'top-right' });
                }
            }
            return true;
        } catch {
            toast({ title: 'Failed to add comment', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
            return false;
        } finally {
            setCommentSubmitting(false);
        }
    }, [commentIssue, userProfile, loadComments, toast]);

    /**
     * Edit an existing comment. Returns true on success so the CommentItem can exit edit mode.
     */
    const handleUpdateComment = useCallback(async (commentId, text) => {
        const trimmed = (text || '').trim();
        if (!trimmed || !commentId || !commentIssue?.issueId) return false;
        try {
            const res = await apiService.updateIssueComment(commentIssue.issueId, commentId, trimmed);
            if (res.success) {
                setComments(prev => prev.map(c => (c.id === commentId ? { ...c, comment: trimmed } : c)));
                return true;
            }
            toast({ title: 'Failed to update comment', description: res.message || '', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
            return false;
        } catch {
            toast({ title: 'Failed to update comment', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
            return false;
        }
    }, [commentIssue, toast]);

    // Current user's email — used to allow editing only one's own comments.
    const currentUserEmail = (localStorage.getItem('userEmail') || userProfile?.email || '').toLowerCase();

    /**
     * Trigger a one-time, on-demand run of the issue's data query (backend resolves the
     * query, runs it via the dynamic-report reload channel, and stores the result). The
     * result arrives after the client's next poll; we poll for it and render the grid.
     */
    const runDataQuery = useCallback(async () => {
        const issue = messageIssue;
        if (!issue?.issueId) return;
        const baselineExec = dqResult?.executedAt || null;
        setDqRunning(true);
        try {
            const res = await apiService.runIssueDataQuery(issue.issueId);
            if (res?.data?.hasDataQuery === false) {
                setDqHasQuery(false);
                setDqRunning(false);
                return;
            }
            toast({
                title: 'Data Query Queued',
                description: 'Running on the client — data will be available within ~2 minutes.',
                status: 'info',
                duration: 6000,
                isClosable: true,
            });
            // Poll for a fresh result
            let attempts = 0;
            const maxAttempts = 24; // ~2 min at 5s
            if (dqPollRef.current) { clearInterval(dqPollRef.current); }
            dqPollRef.current = setInterval(async () => {
                attempts += 1;
                try {
                    const r = await apiService.getIssueDataQueryResult(issue.issueId);
                    const data = r?.data || {};
                    if (data.executed && Array.isArray(data.rows) && data.executedAt !== baselineExec) {
                        clearInterval(dqPollRef.current); dqPollRef.current = null;
                        setDqResult({ columns: deriveColumns(data.rows), rows: data.rows, executedAt: data.executedAt });
                        setDqRunning(false);
                    } else if (attempts >= maxAttempts) {
                        clearInterval(dqPollRef.current); dqPollRef.current = null;
                        setDqRunning(false);
                    }
                } catch {
                    if (attempts >= maxAttempts) {
                        clearInterval(dqPollRef.current); dqPollRef.current = null;
                        setDqRunning(false);
                    }
                }
            }, 5000);
        } catch (err) {
            setDqRunning(false);
            toast({
                title: 'Failed',
                description: err.message || 'Failed to run data query',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    }, [messageIssue, dqResult, toast]);

    /**
     * Handle action button click
     */
    const handleAction = useCallback((issue, action) => {
        setSelectedIssue(issue);
        setActionType(action);
        onOpen();
    }, [onOpen]);

    /**
 * Copy a data query to the clipboard.
     */
    const handleCopyDataQuery = useCallback((dataQuery) => {
        navigator.clipboard.writeText(dataQuery || '').then(() => {
            toast({
                title: 'Copied!',
                description: 'Data query copied to clipboard',
                status: 'success',
                duration: 2000,
                isClosable: true
            });
        }).catch(() => {
            toast({
                title: 'Copy Failed',
                description: 'Failed to copy to clipboard',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        });
    }, [toast]);

    /**
     * Execute action with proper API calls
     */
    const executeAction = useCallback(async () => {
        if (!selectedIssue || !actionType) return;

        try {
            setActionLoading(true);
            const userEmail = localStorage.getItem('userEmail') || userProfile?.email;

            let response;
            switch (actionType) {
                case 'fix':
                    response = await apiService.markReportTriggeredIssueAsFixed(selectedIssue.issueId, userEmail);
                    break;
                case 'notIssue':
                    response = await apiService.markReportTriggeredIssueAsNotAnIssue(selectedIssue.issueId, userEmail);
                    break;
                case 'delete':
                    response = await apiService.deleteReportTriggeredIssue(selectedIssue.issueId);
                    break;
                default:
                    throw new Error('Invalid action type');
            }

            if (response.success) {
                // Map the internal action key onto a stable verb for the usage report.
                const USAGE_ACTIONS = { fix: 'MARK_FIXED', notIssue: 'MARK_NOT_AN_ISSUE', delete: 'DELETE' };
                if (USAGE_ACTIONS[actionType]) {
                    usageTracker.action('Issues', USAGE_ACTIONS[actionType], selectedIssue?.clientName || undefined);
                }
                toast({
                    title: 'Success',
                    description: getActionSuccessMessage(actionType),
                    status: 'success',
                    duration: 3000,
                    isClosable: true
                });

                // Refresh data
                loadIssues();
                loadTabCounts();
            } else {
                throw new Error(response.message || 'Action failed');
            }

        } catch (error) {
            console.error('Action failed:', error);
            toast({
                title: 'Error',
                description: error.message || 'Action failed',
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setActionLoading(false);
            onClose();
        }
    }, [selectedIssue, actionType, userProfile, loadIssues, loadTabCounts, toast, onClose]);

    /**
     * Get action success message
     */
    const getActionSuccessMessage = (action) => {
        switch (action) {
            case 'fix': return 'Issue marked as fixed';
            case 'notIssue': return 'Issue marked as not an issue';
            case 'delete': return 'Issue deleted successfully';
            default: return 'Action completed successfully';
        }
    };

    /**
     * Setup auto-refresh with manual control
     */
    const setupAutoRefresh = useCallback(() => {
        if (refreshInterval.current) {
            clearInterval(refreshInterval.current);
        }
        
        if (autoRefreshEnabled) {
            refreshInterval.current = setInterval(() => {
                loadIssues(activeTab, true); // Auto-refresh
                loadTabCounts();
            }, 30000); // 30 seconds
        }
    }, [activeTab, autoRefreshEnabled, loadIssues, loadTabCounts]);

    /**
     * Manual refresh
     */
    const handleManualRefresh = useCallback(() => {
        loadIssues();
        loadTabCounts();
        toast({
            title: 'Refreshed',
            description: 'Data refreshed successfully',
            status: 'info',
            duration: 2000,
            isClosable: true
        });
    }, [loadIssues, loadTabCounts, toast]);

    /**
     * Handle URL parameters from Analytics navigation with proper timing
     */
    // useEffect(() => {
    //     if (urlParamsProcessed) return;

    //     const urlParams = new URLSearchParams(window.location.search);
    //     const clientId = urlParams.get('clientId');
    //     const clientName = urlParams.get('clientName');
    //     const tab = urlParams.get('tab');
        
    //     let hasUrlParams = false;

    //     // Apply client filter if provided
    //     if (clientId) {
    //         setUserFilters(prev => ({ ...prev, client_id: clientId }));
    //         hasUrlParams = true;
    //     }
        
    //     // Switch to specific tab if provided
    //     if (tab) {
    //         const tabIndex = parseInt(tab);
    //         if (tabIndex >= 0 && tabIndex < tabs.length) {
    //             setActiveTab(tabIndex);
    //             hasUrlParams = true;
    //         }
    //     }

    //     setUrlParamsProcessed(true);
    // }, [urlParamsProcessed]);

//vishnu--modified
    useEffect(() => {
        if (urlParamsProcessed) return;
        
        const clientId = searchParams.get('clientId');
        const clientName = searchParams.get('clientName');
        const tab = searchParams.get('tab');
        
        let hasUrlParams = false;
        
        // Apply client filter if provided
        if (clientId) {
            setUserFilters(prev => ({ ...prev, client_id: clientId }));
            hasUrlParams = true;
        }
        
        // Switch to a specific tab if provided. Accepts a tab key ("issues", "tickets",
        // …) and falls back to a numeric index for older links. Prefer keys: indices
        // shift whenever a tab is added or removed.
        if (tab) {
            const byKey = tabs.findIndex(t => t.key === tab);
            const tabIndex = byKey >= 0 ? byKey : parseInt(tab, 10);
            if (Number.isInteger(tabIndex) && tabIndex >= 0 && tabIndex < tabs.length) {
                setActiveTab(tabIndex);
                hasUrlParams = true;
            }
        }
        
        setUrlParamsProcessed(true);
    }, [urlParamsProcessed]);
    /**
     * Initialize component
     */
    useEffect(() => {
        if (!authServiceReady) return;
        
        loadFilterOptions();
        
        // Only load data after URL parameters are processed
        if (urlParamsProcessed) {
            loadIssues();
            loadTabCounts();
        }

        return () => {
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
            }
        };
    }, [authServiceReady, urlParamsProcessed, loadFilterOptions, loadIssues, loadTabCounts]);

    /**
     * Handle tab change effect
     */
    useEffect(() => {
        if (authServiceReady && urlParamsProcessed) {
            loadIssues();
        }
    }, [activeTab, pagination.page, pagination.size, userFilters, authServiceReady, urlParamsProcessed, loadIssues]);

    /**
     * Handle filter change effect
     */
    useEffect(() => {
        if (authServiceReady && urlParamsProcessed) {
            loadTabCounts();
        }
    }, [userFilters, authServiceReady, urlParamsProcessed, loadTabCounts]);

    /**
     * Handle auto refresh toggle effect
     */
    useEffect(() => {
        setupAutoRefresh();
        
        return () => {
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
            }
        };
    }, [setupAutoRefresh]);

    /**
     * Render action buttons for table rows
     */
    const renderActionButtons = useCallback((issue) => {
        const currentTab = tabs[activeTab];

        if (currentTab.key === 'issues') {
            return (
                <ButtonGroup size="sm" spacing={2}>
                    <Tooltip
                        label={issue.redmineIssueId
                            ? `Ticket #${issue.redmineIssueId} created`
                            : 'Create ticket'}
                        hasArrow
                    >
                        <IconButton
                            icon={<MdConfirmationNumber />}
                            colorScheme={issue.redmineIssueId ? 'green' : 'gray'}
                            variant="ghost"
                            onClick={() => handleCommentsClick(issue)}
                            aria-label={issue.redmineIssueId
                                ? `Ticket #${issue.redmineIssueId} created`
                                : 'Create ticket'}
                        />
                    </Tooltip>
                    <Tooltip label="Mark as Fixed" hasArrow>
                        <IconButton
                            icon={<MdCheckCircle />}
                            colorScheme="green"
                            variant="ghost"
                            onClick={() => handleAction(issue, 'fix')}
                            aria-label="Mark as Fixed"
                        />
                    </Tooltip>
                    <Tooltip label="Mark as Not an Issue" hasArrow>
                        <IconButton
                            icon={<MdCancel />}
                            colorScheme="orange"
                            variant="ghost"
                            onClick={() => handleAction(issue, 'notIssue')}
                            aria-label="Mark as Not an Issue"
                        />
                    </Tooltip>
                    {/* Delete deprecated on the Issues tab — an open issue is resolved via
                        Mark as Fixed / Not an Issue rather than deleted. Still available on
                        the Fixed / Not an Issue / Tickets tabs.
                    {isAdmin && (
                        <Tooltip label="Delete Issue" hasArrow>
                            <IconButton
                                icon={<MdDelete />}
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleAction(issue, 'delete')}
                                aria-label="Delete Issue"
                            />
                        </Tooltip>
                    )}
                    */}
                </ButtonGroup>
            );
        }

        // For fixed, notIssue and tickets tabs, only show delete for admin (and only if actions column exists)
        if (isAdmin && currentTab.columns.includes('actions')) {
            return (
                <Tooltip label="Delete Issue" hasArrow>
                    <IconButton
                        icon={<MdDelete />}
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleAction(issue, 'delete')}
                        aria-label="Delete Issue"
                        size="sm"
                    />
                </Tooltip>
            );
        }

        return null;
    }, [activeTab, isAdmin, handleAction, handleCommentsClick]);

    /**
     * Render table columns based on tab configuration (issue ID column removed)
     */
    const renderTableHeader = useCallback(() => {
        const currentTab = tabs[activeTab];
        const columnConfig = {
            client: { label: 'Client', width: '200px' },
            report: { label: 'Report', width: '250px' },
            message: { label: 'Message', width: '300px' },
            ticketStatus: { label: 'Ticket Status', width: '130px' },
            ticket: { label: 'Ticket', width: '140px' },
            issueTakenBy: { label: 'Assigned To', width: '150px' },
            fixedBy: { label: 'Fixed By', width: '150px' },
            notAnIssueBy: { label: 'Not Issue By', width: '150px' },
            created: { label: 'Created', width: '160px' },
            actions: { label: 'Actions', width: '120px' }
        };

        return (
            <Tr>
                {currentTab.columns.map(column => (
                    <Th key={column} width={columnConfig[column]?.width}>
                        {columnConfig[column]?.label}
                    </Th>
                ))}
            </Tr>
        );
    }, [activeTab]);

    /**
     * Render table row based on tab configuration (issue ID cell removed)
     */
    const renderTableRow = useCallback((issue) => {
        const currentTab = tabs[activeTab];
        
        const cellRenderers = {
            client: () => (
                <Text fontWeight="medium" noOfLines={1}>
                    {issue.clientName || 'Unknown Client'}
                </Text>
            ),
            report: () => (
                <Text noOfLines={2} maxW="240px">
                    {issue.reportDescription || 'Heartbeat'}
                </Text>
            ),
            message: () => (
                <Text 
                    noOfLines={3} 
                    maxW="290px" 
                    cursor="pointer"
                    color="blue.500"
                    _hover={{ textDecoration: 'underline' }}
                    onClick={() => handleMessageClick(issue)}
                >
                    {issue.message}
                </Text>
            ),
            // The Redmine ticket's own status. Only present on the Tickets tab, which
            // shows it instead of the Prism issue status.
            ticketStatus: () => (
                issue.redmineIssueId ? (
                    <RedmineStatusBadge
                        status={issue.redmineStatus}
                        isClosed={issue.redmineStatusIsClosed}
                        compact
                    />
                ) : (
                    <Text fontSize="xs" color="gray.500">—</Text>
                )
            ),
            ticket: () => (
                issue.redmineIssueId ? (
                    <HStack spacing={1}>
                        {issue.redmineIssueUrl ? (
                            <Link
                                href={issue.redmineIssueUrl}
                                isExternal
                                color="blue.500"
                                fontWeight="600"
                                fontSize="xs"
                                whiteSpace="nowrap"
                            >
                                #{issue.redmineIssueId}
                            </Link>
                        ) : (
                            <Text fontWeight="600" fontSize="xs" whiteSpace="nowrap">#{issue.redmineIssueId}</Text>
                        )}
                        {/* Skip the badge when the tab already has a Ticket Status column. */}
                        {!currentTab.columns.includes('ticketStatus') && (
                            <RedmineStatusBadge
                                status={issue.redmineStatus}
                                isClosed={issue.redmineStatusIsClosed}
                                compact
                            />
                        )}
                        <Tooltip
                            label={issue.redmineStatusSyncedAtUtc
                                ? `Last synced ${formatUtcToLocal(issue.redmineStatusSyncedAtUtc)}`
                                : 'Never synced'}
                            hasArrow
                        >
                            <IconButton
                                aria-label="Sync ticket status"
                                icon={<MdRefresh />}
                                size="xs"
                                variant="ghost"
                                colorScheme="blue"
                                isLoading={syncingStatusFor === issue.issueId}
                                onClick={() => handleSyncRedmineStatus(issue)}
                            />
                        </Tooltip>
                    </HStack>
                ) : (
                    <Text fontSize="xs" color="gray.500">—</Text>
                )
            ),
            issueTakenBy: () => (
                <Text fontSize="xs" noOfLines={1}>
                    {issue.issueTakenBy || '-'}
                </Text>
            ),
            fixedBy: () => (
                <Text fontSize="xs" noOfLines={1}>
                    {issue.fixedBy || '-'}
                </Text>
            ),
            notAnIssueBy: () => (
                <Text fontSize="xs" noOfLines={1}>
                    {issue.notAnIssueBy || '-'}
                </Text>
            ),
            created: () => (
                <Text fontSize="xs">
                    {formatDateTime(issue.createdAtUtc)}
                </Text>
            ),
            actions: () => renderActionButtons(issue)
        };

        return (
            <Tr key={issue.issueId} _hover={{ bg: hoverBg }}>
                {currentTab.columns.map(column => (
                    <Td key={column}>
                        {cellRenderers[column]()}
                    </Td>
                ))}
            </Tr>
        );
    }, [activeTab, handleMessageClick, renderActionButtons, hoverBg, syncingStatusFor, handleSyncRedmineStatus]);

    /**
     * Render loading state
     */
    if (loading && !issues.length) {
        return (
            <Flex justify="center" align="center" minH="400px">
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.500" thickness="4px" />
                    <Text color={textColor}>Loading issues...</Text>
                </VStack>
            </Flex>
        );
    }

    /**
     * Render error state
     */
    if (error && !issues.length) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                    {error}. Try refreshing manually.
                </AlertDescription>
            </Alert>
        );
    }

    /**
     * Main content component that can be rendered in normal or fullscreen mode
     */
    const MainContent = () => (
        <Box>
            <Grid templateColumns={showFilters ? { base: '1fr', lg: 'minmax(0, 1fr) 300px' } : '1fr'} gap={6}>
                {/* Left Column - Main Content */}
                <GridItem minW={0}>
                    <VStack spacing={6} align="stretch">
                        {/* ENHANCED: Page Header with new controls */}
                        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                            <Box>
                                <Heading size="lg" color={headingColor} mb={2}>
                                    Issues Management
                                </Heading>
                                <Text color={textColor} fontSize="sm">
                                    Track and manage report triggered issues across all clients
                                </Text>
                            </Box>
                            
                            <HStack spacing={2} wrap="wrap">
                                {/* Show Internal Toggle */}
                                <FormControl display="flex" alignItems="center" width="auto">
                                    <FormLabel htmlFor="show-internal" mb="0" fontSize="sm" mr={2}>
                                        Show Internal
                                    </FormLabel>
                                    <Switch
                                        id="show-internal"
                                        isChecked={showInternal}
                                        onChange={(e) => handleShowInternalToggle(e.target.checked)}
                                        colorScheme="brand"
                                        size="sm"
                                    />
                                </FormControl>

                                {/* Auto Refresh Toggle */}
                                <FormControl display="flex" alignItems="center" width="auto">
                                    <FormLabel htmlFor="auto-refresh" mb="0" fontSize="sm" mr={2}>
                                        Auto Refresh
                                    </FormLabel>
                                    <Switch
                                        id="auto-refresh"
                                        isChecked={autoRefreshEnabled}
                                        onChange={toggleAutoRefresh}
                                        colorScheme="brand"
                                        size="sm"
                                    />
                                </FormControl>

                                {/* Manual Refresh Button */}
                                <Button
                                    leftIcon={<MdRefresh />}
                                    onClick={handleManualRefresh}
                                    variant="outline"
                                    size="sm"
                                >
                                    Refresh
                                </Button>

                                {/* Filter Toggle Button */}
                                <Tooltip label={showFilters ? "Hide Filters" : "Show Filters"} hasArrow>
                                    <IconButton
                                        icon={showFilters ? <MdVisibilityOff /> : <MdVisibility />}
                                        onClick={toggleFilters}
                                        variant="outline"
                                        size="sm"
                                        aria-label="Toggle Filters"
                                    />
                                </Tooltip>

                                {/* Fullscreen Toggle Button */}
                                <Tooltip label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"} hasArrow>
                                    <IconButton
                                        icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
                                        onClick={toggleFullscreen}
                                        variant="outline"
                                        size="sm"
                                        aria-label="Toggle Fullscreen"
                                    />
                                </Tooltip>
                            </HStack>
                        </Flex>

                        {/* Tabs */}
                        <Tabs index={activeTab} onChange={handleTabChange} variant="enclosed">
                            <TabList>
                                {tabs.map((tab, index) => (
                                    <Tab key={tab.key}>
                                        <HStack spacing={2}>
                                            <Text>{tab.label}</Text>
                                            <Badge colorScheme="brand" variant="subtle">
                                                {formatNumber(tabCounts[index])}
                                            </Badge>
                                        </HStack>
                                    </Tab>
                                ))}
                            </TabList>

                            <TabPanels>
                                {tabs.map((tab, index) => (
                                    <TabPanel key={tab.key} px={0} py={0}>
                                        <Card bg={cardBg} borderColor={borderColor} mt={4}>
                                            <CardBody>
                                                {issues.length > 0 ? (
                                                    <VStack spacing={4} align="stretch">
                                                        {/* Table */}
                                                        <TableContainer>
                                                            <Table variant="simple" size="sm">
                                                                <Thead>
                                                                    {renderTableHeader()}
                                                                </Thead>
                                                                <Tbody>
                                                                    {issues.map(issue => renderTableRow(issue))}
                                                                </Tbody>
                                                            </Table>
                                                        </TableContainer>

                                                        {/* Pagination */}
                                                        <PaginationControls
                                                            currentPage={pagination.page}
                                                            totalPages={pagination.totalPages}
                                                            totalItems={pagination.total}
                                                            itemsPerPage={pagination.size}
                                                            onPageChange={handlePageChange}
                                                            onPageSizeChange={handlePageSizeChange}
                                                        />
                                                    </VStack>
                                                ) : (
                                                    <Flex justify="center" align="center" minH="200px">
                                                        <VStack spacing={4}>
                                                            <MdDescription size={48} color="gray" />
                                                            <Text color={textColor} textAlign="center">
                                                                No issues found for {tab.label.toLowerCase()}
                                                            </Text>
                                                        </VStack>
                                                    </Flex>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </TabPanel>
                                ))}
                            </TabPanels>
                        </Tabs>
                    </VStack>
                </GridItem>

                {/* Right Column - Filters - Conditionally rendered */}
                {showFilters && (
                    <GridItem>
                        <Card bg={cardBg} borderColor={borderColor} position="sticky" top="104px" h="fit-content">
                            <CardHeader pb={2}>
                                <HStack justify="space-between">
                                    <HStack>
                                        <MdFilterList />
                                        <Text fontWeight="semibold">Filters</Text>
                                    </HStack>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        onClick={handleClearFilters}
                                        leftIcon={<MdClose />}
                                    >
                                        Clear
                                    </Button>
                                </HStack>
                            </CardHeader>
                            <CardBody pt={0}>
                                <VStack spacing={4} align="stretch">
                                    {tabs[activeTab]?.key === 'tickets' && (
                                        <Box>
                                            <Text fontSize="sm" mb={2} color={textColor} fontWeight="medium">Ticket Status</Text>
                                            <Select
                                                value={ticketClosedFilter}
                                                onChange={(e) => setTicketClosedFilter(e.target.value)}
                                                size="sm"
                                            >
                                                <option value="open">Open</option>
                                                <option value="closed">Closed</option>
                                                <option value="all">All</option>
                                            </Select>
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                Tickets not yet synced count as open.
                                            </Text>
                                        </Box>
                                    )}
                                    <Box>
                                        <Text fontSize="sm" mb={2} color={textColor} fontWeight="medium">Client</Text>
                                        <Select
                                            placeholder="All Clients"
                                            value={userFilters.client_id}
                                            onChange={(e) => handleFilterChange('client_id', e.target.value)}
                                            size="sm"
                                        >
                                            {clients.map(client => (
                                                <option key={client.clientId} value={client.clientId}>
                                                    {client.clientName}
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontSize="sm" mb={2} color={textColor} fontWeight="medium">Report</Text>
                                        <Select
                                            placeholder="All Reports"
                                            value={userFilters.report_code}
                                            onChange={(e) => handleFilterChange('report_code', e.target.value)}
                                            size="sm"
                                        >
                                            {reports.map(report => (
                                                <option key={report.reportCode} value={report.reportCode}>
                                                    {report.reportDescription}
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>

                                    <Box>
                                        <Text fontSize="sm" mb={2} color={textColor} fontWeight="medium">Created Date Range</Text>
                                        <style>{datePickerCustomStyles}</style>
                                        <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateRangeChange}
                                            isClearable={true}
                                            placeholderText="All Dates"
                                            dateFormat="dd/MM/yyyy"
                                            className="custom-datepicker"
                                            maxDate={new Date()}
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                            calendarStartDay={1}
                                            wrapperClassName="custom-datepicker-wrapper"
                                        />
                                    </Box>
                                    
                                    {/* <Box>
                                        <Text fontSize="sm" mb={2} color={textColor} fontWeight="medium">Search</Text>
                                        <InputGroup size="sm">
                                            <InputLeftElement>
                                                <MdSearch color={textColor} />
                                            </InputLeftElement>
                                            <Input
                                                placeholder="Search issues..."
                                                value={userFilters.search}
                                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                            />
                                        </InputGroup>
                                    </Box> */}

                                    <Divider />

                                    {/* Current Tab Info */}
                                    <Box>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium" mb={2}>
                                            Current Tab: {tabs[activeTab]?.label}
                                        </Text>
                                        <Text fontSize="xs" color={textColor}>
                                            {tabs[activeTab]?.key === 'issues' && 'Shows all open issues'}
                                            {tabs[activeTab]?.key === 'fixed' && 'Shows issues marked as fixed'}
                                            {tabs[activeTab]?.key === 'notIssue' && 'Shows issues marked as not issues'}
                                            {tabs[activeTab]?.key === 'tickets' && 'Shows issues with a Redmine ticket'}
                                        </Text>
                                    </Box>

                                    {/* Applied Filters Summary */}
                                    <Box>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium" mb={2}>
                                            Applied Filters
                                        </Text>
                                        <VStack spacing={1} align="start">
                                            {userFilters.client_id && (
                                                <Badge colorScheme="blue" variant="subtle" fontSize="xs" maxWidth="100%" whiteSpace="normal" textAlign="center" noOfLines={2}>
                                                    Client: {clients.find(c => c.clientId.toString() === userFilters.client_id)?.clientName || userFilters.client_id}
                                                </Badge>
                                            )}
                                            {userFilters.report_code && (
                                                <Badge colorScheme="green" variant="subtle" fontSize="xs" maxWidth="100%" whiteSpace="normal" textAlign="center" noOfLines={2}>
                                                    Report: {reports.find(r => r.reportCode.toString() === userFilters.report_code)?.reportDescription || userFilters.report_code}
                                                </Badge>
                                            )}
                                            {(userFilters.startDate && userFilters.endDate) && (
                                                <Badge colorScheme="purple" variant="subtle" fontSize="xs" maxWidth="100%" whiteSpace="normal" textAlign="center" noOfLines={2}>
                                                    Date: {startDate?.toLocaleDateString('en-GB')} - {endDate?.toLocaleDateString('en-GB')}
                                                </Badge>
                                            )}
                                            {userFilters.search && (
                                                <Badge colorScheme="pink" variant="subtle" fontSize="xs" maxWidth="100%" whiteSpace="normal" textAlign="center" noOfLines={2}>
                                                    Search: {userFilters.search}
                                                </Badge>
                                            )}
                                            {!userFilters.client_id && !userFilters.report_code && !userFilters.search && !userFilters.startDate && !userFilters.endDate && (
                                                <Text fontSize="xs" color={textColor}>
                                                    No user filters applied
                                                </Text>
                                            )}
                                        </VStack>
                                    </Box>

                                    {/* Filter Persistence Notice */}
                                    <Box bg={persistenceNoticeBg} p={3} borderRadius="md">
                                        <Text fontSize="xs" color={persistenceNoticeTextPrimary} fontWeight="medium" mb={1}>
                                            Filter Persistence
                                        </Text>
                                        <Text fontSize="xs" color={persistenceNoticeTextSecondary}>
                                            Client, Report, and Date Range filters remain active when switching tabs. Use "Clear" button to reset all filters.
                                        </Text>
                                    </Box>
                                </VStack>
                            </CardBody>
                        </Card>
                    </GridItem>
                )}
            </Grid>

            {/* Action Confirmation Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        Confirm Action
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>
                            Are you sure you want to {' '}
                            {actionType === 'take' && 'take this issue'}
                            {actionType === 'fix' && 'mark this issue as fixed'}
                            {actionType === 'notIssue' && 'mark this issue as not an issue'}
                            {actionType === 'delete' && 'delete this issue'}
                            ?
                        </Text>
                        {selectedIssue && (
                            <Box mt={4} p={3} bg={hoverBg} borderRadius="md">
                                <Text fontSize="sm" fontWeight="medium">
                                    {selectedIssue.clientName} - {selectedIssue.reportDescription}
                                </Text>
                                <Text fontSize="xs" color={textColor} mt={1} noOfLines={3}>
                                    {selectedIssue.message}
                                </Text>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" onClick={onClose} mr={3}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme={
                                actionType === 'delete' ? 'red' :
                                actionType === 'fix' ? 'green' :
                                actionType === 'notIssue' ? 'orange' :
                                'blue'
                            }
                            onClick={executeAction}
                            isLoading={actionLoading}
                        >
                            Confirm
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Message Display Modal */}
            <Modal isOpen={isMessageOpen} onClose={handleMessageClose} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack>
                            <MdMessage />
                            <Text>Issue Message</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Box
                            p={4}
                            bg={hoverBg}
                            borderRadius="md"
                            fontFamily="mono"
                            fontSize="sm"
                            whiteSpace="pre-wrap"
                            overflowWrap="break-word"
                            maxH="400px"
                            overflowY="auto"
                        >
                            {messageToShow}
                        </Box>

                        {/* ── Execute Data Query (runs once via the dynamic-report reload channel) ── */}
                        {messageIssue && (
                            <Box mt={4}>
                                <Divider mb={3} />
                                <HStack justify="space-between" align="center" mb={3}>
                                    <Box>
                                        <HStack spacing={1}>
                                            <Text fontSize="sm" fontWeight="600">Data Query</Text>
                                            {messageIssue.dataQuery && (
                                                <Tooltip
                                                    label={showDataQuerySql ? 'Hide the query' : 'Show the query'}
                                                    hasArrow
                                                >
                                                    <IconButton
                                                        aria-label={showDataQuerySql ? 'Hide the data query' : 'Show the data query'}
                                                        icon={<MdInfoOutline />}
                                                        size="xs"
                                                        variant="ghost"
                                                        colorScheme="purple"
                                                        onClick={() => setShowDataQuerySql(prev => !prev)}
                                                    />
                                                </Tooltip>
                                            )}
                                        </HStack>
                                        <Text fontSize="xs" color="gray.500">
                                            Runs the report's data query once on the client and shows the result.
                                        </Text>
                                    </Box>
                                    {dqHasQuery !== false && (
                                        <Button
                                            leftIcon={<MdPlayArrow />}
                                            colorScheme="purple"
                                            size="sm"
                                            onClick={runDataQuery}
                                            isDisabled={dqLoading || dqRunning}
                                            isLoading={dqRunning}
                                            loadingText="Running…"
                                        >
                                            {dqResult ? 'Rerun Data Query' : 'Run Data Query'}
                                        </Button>
                                    )}
                                </HStack>

                                {/* The query itself, revealed by the info icon above. */}
                                {showDataQuerySql && messageIssue.dataQuery && (
                                    <Box mb={3}>
                                        <Box
                                            p={3}
                                            bg={hoverBg}
                                            borderRadius="md"
                                            fontFamily="mono"
                                            fontSize="xs"
                                            whiteSpace="pre-wrap"
                                            overflowWrap="break-word"
                                            maxH="240px"
                                            overflowY="auto"
                                            border="1px solid"
                                            borderColor={borderColor}
                                        >
                                            {messageIssue.dataQuery}
                                        </Box>
                                        <Flex justify="flex-end" mt={2}>
                                            <Button
                                                leftIcon={<MdContentCopy />}
                                                size="xs"
                                                variant="ghost"
                                                colorScheme="blue"
                                                onClick={() => handleCopyDataQuery(messageIssue.dataQuery)}
                                            >
                                                Copy
                                            </Button>
                                        </Flex>
                                    </Box>
                                )}

                                {dqLoading && (
                                    <HStack color="gray.500" fontSize="sm" mb={2}>
                                        <Spinner size="sm" /><Text>Loading…</Text>
                                    </HStack>
                                )}

                                {dqHasQuery === false && (
                                    <Alert status="warning" borderRadius="md" fontSize="sm">
                                        <AlertIcon />
                                        <AlertDescription>No data query found for this report.</AlertDescription>
                                    </Alert>
                                )}

                                {dqRunning && (
                                    <Alert status="info" borderRadius="md" fontSize="sm">
                                        <AlertIcon />
                                        <AlertDescription>
                                            Queued — running on the client. Data will be available within ~2 minutes;
                                            this view updates automatically.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {dqResult && (
                                    <Box mt={3}>
                                        <HStack justify="space-between" mb={2}>
                                            <Badge colorScheme="green" variant="subtle">
                                                {dqResult.rows.length} row{dqResult.rows.length === 1 ? '' : 's'}
                                            </Badge>
                                            {dqResult.executedAt && (
                                                <Text fontSize="xs" color="gray.500">
                                                    Executed: {formatUtcToLocal(dqResult.executedAt)}
                                                </Text>
                                            )}
                                        </HStack>
                                        {dqResult.rows.length === 0 ? (
                                            <Box p={3} bg={hoverBg} borderRadius="md" fontSize="sm" color="gray.500">
                                                Query ran successfully and returned no rows.
                                            </Box>
                                        ) : (
                                            <TableContainer maxH="300px" overflowY="auto" borderWidth="1px" borderColor={borderColor} borderRadius="md">
                                                <Table size="sm" variant="simple">
                                                    <Thead position="sticky" top={0} bg={cardBg} zIndex={1}>
                                                        <Tr>
                                                            {dqResult.columns.map(col => (
                                                                <Th key={col} whiteSpace="nowrap">{col}</Th>
                                                            ))}
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        {dqResult.rows.map((row, ri) => (
                                                            <Tr key={ri}>
                                                                {dqResult.columns.map(col => (
                                                                    <Td key={col} fontSize="xs" whiteSpace="nowrap">
                                                                        {row[col] == null ? '' : String(row[col])}
                                                                    </Td>
                                                                ))}
                                                            </Tr>
                                                        ))}
                                                    </Tbody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* ── Ticket & comments (handled in the Comments modal) ── */}
                        {messageIssue && (
                            <Box mt={4}>
                                <Divider mb={3} />
                                {/* Stacked, not side by side: the heading, the ticket line and
                                    the button together are wider than a size="lg" modal, and
                                    competing for one row squeezed the text into a column. */}
                                <VStack align="stretch" spacing={3}>
                                    <Box>
                                        <Text fontSize="sm" fontWeight="600">Ticket &amp; Comments</Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {messageIssue.redmineIssueId
                                                ? 'View the ticket raised for this issue, or add a comment.'
                                                : "Raise a Redmine ticket in the client's project, or add a comment."}
                                        </Text>
                                    </Box>

                                    {messageIssue.redmineIssueId && (
                                        <Box
                                            px={3}
                                            py={2}
                                            bg={hoverBg}
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor={borderColor}
                                        >
                                            <RedmineTicketLink
                                                redmineId={messageIssue.redmineIssueId}
                                                redmineUrl={messageIssue.redmineIssueUrl}
                                                status={messageIssue.redmineStatus}
                                                isClosed={messageIssue.redmineStatusIsClosed}
                                                syncedAt={messageIssue.redmineStatusSyncedAtUtc}
                                                onSync={() => handleSyncRedmineStatus(messageIssue)}
                                                syncing={syncingStatusFor === messageIssue.issueId}
                                            />
                                        </Box>
                                    )}

                                    <Flex justify="flex-end">
                                        <Button
                                            leftIcon={<MdConfirmationNumber />}
                                            colorScheme="blue"
                                            size="sm"
                                            onClick={() => handleTicketFromMessage(messageIssue)}
                                        >
                                            {messageIssue.redmineIssueId ? 'Add Comments' : 'Create Ticket'}
                                        </Button>
                                    </Flex>
                                </VStack>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={handleMessageClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ── Comments Modal (separate action) ─────────────────────────────── */}
            <Modal isOpen={isCommentsOpen} onClose={handleCommentsClose} size="lg" isCentered isLazy>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack>
                            <MdComment />
                            <Text>Comments</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {commentIssue && (
                            <Text fontSize="sm" color="gray.500" mb={3}>
                                {commentIssue.clientName} — {commentIssue.reportDescription}
                            </Text>
                        )}

                        {commentsLoading ? (
                            <HStack color="gray.500" fontSize="sm" mb={2}>
                                <Spinner size="sm" /><Text>Loading…</Text>
                            </HStack>
                        ) : comments.length === 0 ? (
                            <Text fontSize="sm" color="gray.500" mb={2}>No comments yet. Be the first to add one.</Text>
                        ) : (
                            <VStack align="stretch" spacing={2} mb={3} maxH="320px" overflowY="auto">
                                {comments.map((c, i) => (
                                    <CommentItem
                                        key={c.id || i}
                                        comment={c}
                                        bg={hoverBg}
                                        canEdit={!!currentUserEmail && (c.createdBy || '').toLowerCase() === currentUserEmail}
                                        onSave={handleUpdateComment}
                                    />
                                ))}
                            </VStack>
                        )}

                        <CommentBox
                            submitting={commentSubmitting}
                            issueId={commentIssue?.issueId}
                            alreadyHasTicket={!!commentIssue?.redmineIssueId}
                            redmineId={commentIssue?.redmineIssueId}
                            redmineUrl={commentIssue?.redmineIssueUrl}
                            defaultSubject={commentIssue ? `${commentIssue.reportDescription || 'Issue'} - ${commentIssue.clientName || ''}`.trim() : ''}
                            onSubmit={handleAddComment}
                            redmineStatus={commentIssue?.redmineStatus}
                            redmineStatusIsClosed={commentIssue?.redmineStatusIsClosed}
                            redmineStatusSyncedAt={commentIssue?.redmineStatusSyncedAtUtc}
                            onSyncStatus={commentIssue ? () => handleSyncRedmineStatus(commentIssue) : undefined}
                            syncingStatus={syncingStatusFor === commentIssue?.issueId}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" onClick={handleCommentsClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </Box>
    );

    // Render in fullscreen mode or normal mode
    if (isFullscreen) {
        return (
            <Box
                position="fixed"
                top={0}
                left={0}
                right={0}
                bottom={0}
                zIndex={9999}
                bg={fullscreenBg}
                p={6}
                overflowY="auto"
            >
                <MainContent />
            </Box>
        );
    }

    return <MainContent />;
}

export default Issues;